import { Typography } from '@execonline-inc/execonline-ui';
import {
  AcademicCapIcon,
  BookOpenIcon,
  LockClosedIcon,
  UserGroupIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, SVGProps } from 'react';
import { Tooltip } from 'react-tooltip';
import { CategoryItem, OfferingCategory } from './Types';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

const getCategoryIcon = (category: OfferingCategory): HeroIcon => {
  switch (category) {
    case 'professor-led':
      return AcademicCapIcon;
    case 'on-demand':
      return BookOpenIcon;
    case 'leadership-coaching':
      return UserGroupIcon;
  }
};

interface EntitlementCardProps {
  item: CategoryItem;
}

function EntitlementCard({ item }: EntitlementCardProps) {
  const { category, label, programCount, isLicensed } = item;
  const CategoryIcon = getCategoryIcon(category);
  const tooltipId = `license-tooltip-${category}`;

  return (
    <div
      className={`relative rounded-lg border p-4 text-center bg-muted/50 ${
        !isLicensed ? 'border-border' : 'border-border'
      }`}
      data-intercom-target={`builder-v2-entitlement-card-${category}`}
    >
      {/* Lock badge for unlicensed - dark circle at top right */}
      {!isLicensed && (
        <>
          <div
            data-tooltip-id={tooltipId}
            data-intercom-target="builder-v2-entitlement-card-lock-badge"
            className="absolute top-2 right-2 bg-muted-foreground text-white rounded-full p-1.5 cursor-help"
          >
            <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
          </div>
          <Tooltip
            id={tooltipId}
            place="bottom"
            opacity={1}
            style={{
              backgroundColor: 'var(--foreground)',
              borderRadius: '8px',
              padding: '12px',
              maxWidth: '280px',
              zIndex: 1000,
            }}
          >
            <div className="flex items-start gap-2">
              <XCircleIcon
                className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <div className="font-medium text-white text-sm">
                  Your organization is not licensed for {label}
                </div>
                <p className="text-white/70 text-xs mt-1">
                  Contact ExecOnline if your organization needs access.
                </p>
              </div>
            </div>
          </Tooltip>
        </>
      )}

      {/* Icon - primary when licensed, muted when locked */}
      <div className="flex justify-center mb-2">
        <CategoryIcon
          className={`h-6 w-6 ${isLicensed ? 'text-primary' : 'text-muted-foreground'}`}
          aria-hidden="true"
        />
      </div>

      {/* Program count - large number */}
      <Typography variant="h4" fontWeight="semibold" className="mb-1">
        {programCount}
      </Typography>

      {/* Category label */}
      <Typography variant="caption" className="text-muted-foreground text-center">
        {label}
      </Typography>
    </div>
  );
}

export default EntitlementCard;
